import { useCallback, useEffect, useState } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import { storeItemsToSessionStorage } from "@swiggy-private/portal-dineout-ui";
import { useRouter } from "next/compat/router";
import { STORAGE_KEYS } from "../constants";
import { sendCustomNREvWithHeaders, manageParentOrderIdStorage } from "../utils";
interface TableConfig {
    tableId: string;
    restaurantId: string;
    parentOrderId?: string;
}

export const useTableConfig = () => {
    const router = useRouter();
    const [tableConfig, setTableConfig] = useState<TableConfig>({ tableId: "", restaurantId: "" });
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        try {
            const {
                table_id: tableId,
                restaurant_id: restaurantId,
                parent_order_id: queryParentOrderId,
                show_back: queryShowBack,
                hide_bottom_bar: queryHideBottomBar,
                force_fetch_config: queryForceFetchConfig,
            } = router?.query as {
                table_id?: string;
                restaurant_id?: string;
                parent_order_id?: string;
                show_back?: string;
                hide_bottom_bar?: string;
                force_fetch_config?: string;
            };

            if (tableId && restaurantId) {
                const config = { tableId, restaurantId };
                setTableConfig(config);
                sessionStorage.setItem(STORAGE_KEYS.TABLE_CONFIG, JSON.stringify(config));

                // For V2: If parent_order_id is in query params, store it using existing storage management
                if (queryParentOrderId) {
                    manageParentOrderIdStorage(tableId, restaurantId, queryParentOrderId);
                }
            } else {
                const storedConfig = sessionStorage.getItem(STORAGE_KEYS.TABLE_CONFIG);
                if (storedConfig) {
                    try {
                        const parsedConfig = JSON.parse(storedConfig);
                        setTableConfig(parsedConfig);
                    } catch (_) {
                        sendCustomNREvWithHeaders("session_storage_table_config_error", {});
                    }
                }
            }
            const parentOrderId = sessionStorage.getItem("parent_order_id") ?? undefined;

            if (parentOrderId) {
                setTableConfig((prev) => ({ ...prev, parentOrderId }));
            } else if (queryParentOrderId) {
                setTableConfig((prev) => ({ ...prev, parentOrderId: queryParentOrderId }));
                storeItemsToSessionStorage({ parent_order_id: queryParentOrderId });
            }

            // Store URL query params: show_back, hide_bottom_bar, force_fetch_config (check URL → session → default in utils/sessionFlags)
            const urlFlags: Record<string, string> = {};
            if (!isEmpty(queryShowBack)) urlFlags[STORAGE_KEYS.SHOW_BACK] = queryShowBack;
            if (!isEmpty(queryHideBottomBar)) urlFlags[STORAGE_KEYS.HIDE_BOTTOM_BAR] = queryHideBottomBar;
            if (!isEmpty(queryForceFetchConfig)) urlFlags[STORAGE_KEYS.FORCE_FETCH_CONFIG] = queryForceFetchConfig;
            if (Object.keys(urlFlags).length > 0) {
                storeItemsToSessionStorage(urlFlags);
            }

            setIsLoading(false);
        } catch (_e) {
            setIsLoading(false);
            sendCustomNREvWithHeaders("session_storage_table_config_error", {});
        }
    }, [router?.query]);

    const updateParentOrderId = useCallback((parentOrderId: string) => {
        setTableConfig((prev) => ({ ...prev, parentOrderId }));
    }, []);

    return { tableConfig, isLoading, updateParentOrderId };
};
